import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Space,
  message,
  Descriptions,
  Statistic,
  Row,
  Col,
  Tabs,
  Empty,
  DatePicker,
  Button,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ShopOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const formatMoney = (value) => `NPR ${Number(value || 0).toFixed(2)}`;

const orderStatusColors = {
  pending: 'gold',
  accepted: 'blue',
  packaging: 'cyan',
  out_for_delivery: 'purple',
  delivered: 'green',
  completed: 'green',
  cancelled: 'red',
  refunded: 'red',
};

const CustomerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(null);

  const dateRangeKey = dateRange
    ? `${dateRange[0]?.format('YYYY-MM-DD')}-${dateRange[1]?.format('YYYY-MM-DD')}`
    : 'default';

  const { data, isLoading, refetch } = useQuery(
    ['customer-history', id, dateRangeKey],
    async () => {
      const params = new URLSearchParams();
      if (dateRange && dateRange.length === 2) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      const response = await api.get(`/admin/users/${id}/history?${params.toString()}`);
      return response.data?.data || null;
    },
    { retry: 1 },
  );

  if (!data) {
    return (
      <Card loading={isLoading}>
        <Empty description="No customer data" />
      </Card>
    );
  }

  const { user, orders = [], bookings = [], invoices = [], offline_sales = [], totals = {} } = data;

  const orderColumns = [
    {
      title: 'Order',
      key: 'order',
      width: 110,
      render: (_, record) => (
        <Text strong>
          {record.order_number || `#${record.id}`}
        </Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'order_type',
      width: 90,
      render: (v) => <Tag>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Items',
      key: 'items',
      ellipsis: true,
      render: (_, record) => (
        <Text>
          {(record.items || []).map((i) => `${i.name} x${i.quantity}`).join(', ') || '—'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      width: 110,
      render: (v) => <Tag color={orderStatusColors[v] || 'default'}>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      width: 90,
      render: (v) => <Tag color={v === 'paid' ? 'green' : 'orange'}>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 110,
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
  ];

  const bookingColumns = [
    {
      title: 'Booking #',
      key: 'id',
      width: 90,
      render: (_, record) => <Text strong>#{record.id}</Text>,
    },
    {
      title: 'Service',
      dataIndex: 'service_name',
      ellipsis: true,
    },
    {
      title: 'Date',
      dataIndex: 'booking_date',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (v) => <Tag color={orderStatusColors[v] || 'default'}>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      width: 110,
      align: 'right',
      render: formatMoney,
    },
  ];

  const invoiceColumns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      width: 140,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'billing_type',
      width: 90,
      render: (v) => <Tag>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (v) => <Tag color={v === 'paid' ? 'green' : 'orange'}>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      width: 110,
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Date',
      dataIndex: 'issue_date',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
  ];

  const offlineColumns = [
    {
      title: 'Sale #',
      dataIndex: 'sale_number',
      width: 150,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Items',
      key: 'items',
      ellipsis: true,
      render: (_, record) => (
        <Text>
          {(record.line_items || []).map((i) => `${i.name} x${i.quantity}`).join(', ') || '—'}
        </Text>
      ),
    },
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      width: 100,
      render: (v) => (v ? <Tag>{String(v).toUpperCase()}</Tag> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (v) => <Tag color={v === 'billed' ? 'green' : 'orange'}>{String(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 110,
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Date',
      dataIndex: 'purchase_date',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <UserOutlined /> {user.full_name || user.name}
          </Title>
          <Text type="secondary">
            <Space size={16}>
              {user.email && (
                <span>
                  <MailOutlined /> {user.email}
                </span>
              )}
              {user.phone_number && (
                <span>
                  <PhoneOutlined /> {user.phone_number}
                </span>
              )}
              <Tag>{String(user.user_type || '').toUpperCase()}</Tag>
            </Space>
          </Text>
        </div>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD"
            placeholder={['Start', 'End']}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Refresh
          </Button>
          <Button onClick={() => navigate('/users/customers')}>Back to Customers</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small">
            <Statistic
              title="Online Spend (Orders)"
              value={totals.online_spend || 0}
              precision={2}
              prefix={<ShoppingOutlined />}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small">
            <Statistic
              title="Offline Spend (Counter)"
              value={totals.offline_spend || 0}
              precision={2}
              prefix={<ShopOutlined />}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small">
            <Statistic
              title="Combined Spend"
              value={totals.combined_spend || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small">
            <Statistic
              title="Invoiced Amount"
              value={totals.invoiced_amount || 0}
              precision={2}
              prefix={<FileTextOutlined />}
              suffix="NPR"
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }} size="small">
        <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small">
          <Descriptions.Item label="Orders">
            <ShoppingOutlined /> {totals.order_count || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Bookings">
            <CalendarOutlined /> {totals.booking_count || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Invoices">
            <FileTextOutlined /> {totals.invoice_count || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Offline Sales">
            <ShopOutlined /> {totals.offline_count || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Loyalty Points">{user.loyalty_points || 0}</Descriptions.Item>
          <Descriptions.Item label="Verified">
            <Tag color={user.is_verified ? 'green' : 'red'}>
              {user.is_verified ? 'Verified' : 'Unverified'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Active">
            <Tag color={user.is_active ? 'green' : 'red'}>
              {user.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Membership">
            {user.membership_start_date
              ? `${dayjs(user.membership_start_date).format('DD MMM YYYY')} – ${
                  user.membership_end_date ? dayjs(user.membership_end_date).format('DD MMM YYYY') : 'Open'
                }`
              : 'None'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card>
        <Tabs
          items={[
            {
              key: 'orders',
              label: `Orders (${orders.length})`,
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  columns={orderColumns}
                  dataSource={orders}
                  pagination={{ pageSize: 8, showSizeChanger: false }}
                  scroll={{ x: 800 }}
                />
              ),
            },
            {
              key: 'bookings',
              label: `Bookings (${bookings.length})`,
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  columns={bookingColumns}
                  dataSource={bookings}
                  pagination={{ pageSize: 8, showSizeChanger: false }}
                  scroll={{ x: 700 }}
                />
              ),
            },
            {
              key: 'invoices',
              label: `Invoices (${invoices.length})`,
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  columns={invoiceColumns}
                  dataSource={invoices}
                  pagination={{ pageSize: 8, showSizeChanger: false }}
                  scroll={{ x: 700 }}
                />
              ),
            },
            {
              key: 'offline',
              label: `Offline Sales (${offline_sales.length})`,
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  columns={offlineColumns}
                  dataSource={offline_sales}
                  pagination={{ pageSize: 8, showSizeChanger: false }}
                  scroll={{ x: 800 }}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default CustomerProfile;
